import { redactBase64FromAiPrompt } from './htmlAiRequest.js';

export const AI_CHAT_HISTORY_FIELD = 'aiChatHistory';
export const AI_CHAT_HISTORY_VERSION = 1;
export const MAX_SAVED_AI_TASKS = 20;
export const MAX_SAVED_AI_HISTORY_CHARS = 80_000;

const MESSAGE_ROLES = new Set(['user', 'assistant', 'error']);
const text = (value, maxLength = 2000) => String(
  redactBase64FromAiPrompt(String(value || '')) || ''
).trim().slice(0, maxLength);
const number = (value, fallback = 0) => (
  Number.isFinite(Number(value)) ? Number(value) : fallback
);

const textList = (values, limit = 8) => (Array.isArray(values) ? values : [])
  .slice(0, limit)
  .map((value) => text(value, 600))
  .filter(Boolean);

const sanitizeRecommendation = (value = {}) => ({
  category: text(value.category, 80),
  title: text(value.title || value.label, 180),
  details: text(value.details || value.reason, 800),
  priority: text(value.priority, 40),
  applied: Boolean(value.applied),
});

const sanitizeScorePayload = (payload) => {
  if (payload?.kind !== 'score' || !payload.score || typeof payload.score !== 'object') {
    return undefined;
  }
  const score = payload.score;
  return {
    kind: 'score',
    percentage: number(payload.percentage ?? score.percentage),
    score: {
      score: number(score.score),
      maxScore: number(score.maxScore, 100),
      percentage: number(score.percentage),
      summary: text(score.summary, 1600),
      ratingLabel: text(score.ratingLabel, 120),
      improvements: textList(score.improvements),
      strengths: textList(score.strengths),
      recommendations: (Array.isArray(score.recommendations) ? score.recommendations : [])
        .slice(0, 8)
        .map(sanitizeRecommendation),
      checklist: (Array.isArray(score.checklist) ? score.checklist : [])
        .slice(0, 20)
        .map((item = {}) => ({
          id: text(item.id, 100),
          label: text(item.label, 180),
          passed: Boolean(item.passed),
          pointsAwarded: number(item.pointsAwarded),
          maxPoints: number(item.maxPoints),
          description: text(item.description, 800),
        })),
    },
  };
};

const sanitizeTask = (task = {}) => ({
  type: text(task.type, 80),
  label: text(task.label, 180),
  contextLabel: text(task.contextLabel, 180),
});

const sanitizeMessage = (message = {}) => {
  if (!MESSAGE_ROLES.has(message.role)) return null;
  const content = text(message.content);
  const payload = sanitizeScorePayload(message.payload);
  if (!content && !payload) return null;
  return { role: message.role, content, ...(payload ? { payload } : {}) };
};

export const serializeAiAssistantHistory = ({ runs = [], messages = [] } = {}) => {
  const savedRuns = (Array.isArray(runs) ? runs : [])
    .filter((run) => run?.phase === 'done')
    .map((run) => {
      const runMessages = (Array.isArray(messages) ? messages : [])
        .filter((message) => message.runId === run?.id)
        .map(sanitizeMessage)
        .filter(Boolean)
        .slice(-12);
      if (!runMessages.length) return null;
      return {
        task: sanitizeTask(run.task),
        outcome: text(run.outcome || 'Completed', 180),
        messages: runMessages,
      };
    })
    .filter(Boolean)
    .slice(-MAX_SAVED_AI_TASKS);

  while (
    savedRuns.length > 1
    && JSON.stringify(savedRuns).length > MAX_SAVED_AI_HISTORY_CHARS
  ) {
    savedRuns.shift();
  }

  return { version: AI_CHAT_HISTORY_VERSION, runs: savedRuns };
};

export const hydrateAiAssistantHistory = (value) => {
  if (!value || typeof value !== 'object' || !Array.isArray(value.runs)) {
    return { runs: [], messages: [] };
  }

  const runs = [];
  const messages = [];
  value.runs.slice(-MAX_SAVED_AI_TASKS).forEach((savedRun, runIndex) => {
    const savedMessages = Array.isArray(savedRun?.messages)
      ? savedRun.messages.map(sanitizeMessage).filter(Boolean).slice(-12)
      : [];
    if (!savedMessages.length) return;

    const id = `saved-ai-run-${runIndex + 1}`;
    const task = sanitizeTask(savedRun.task);
    runs.push({
      id,
      task,
      phase: 'done',
      submittedRequest: '',
      previewId: null,
      outcome: text(savedRun.outcome || 'Completed', 180),
      error: '',
    });
    messages.push({
      id: `${id}-task`,
      role: 'task',
      content: task.label || 'AI task',
      taskType: task.type,
      runId: id,
    });
    savedMessages.forEach((message, messageIndex) => {
      messages.push({
        ...message,
        id: `${id}-message-${messageIndex + 1}`,
        runId: id,
        taskType: task.type,
      });
    });
  });

  return { runs, messages };
};
